import React, { useState, useEffect, useRef } from 'react';
import { X, ArrowLeft, RotateCcw, MessageCircle, Send } from 'lucide-react';
import axios from 'axios';
import ChatbotMessage from './ChatbotMessage';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const BungyManChatbot = ({ isOpen, onClose }) => {
  const [currentNode, setCurrentNode] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nodeHistory, setNodeHistory] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      loadWelcomeNode();
    }
  }, [isOpen]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
    }
  }, [conversationHistory, loading]);

  const loadWelcomeNode = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/bungyman/flow`);
      setCurrentNode(response.data);
      setConversationHistory([{
        type: 'bot',
        text: response.data.text,
        timestamp: new Date()
      }]);
      setNodeHistory([]);
    } catch (error) {
      console.error('Failed to load chatbot:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOptionClick = async (option) => {
    // Add user selection to conversation
    const userMessage = {
      type: 'user',
      text: option.text,
      timestamp: new Date()
    };
    setConversationHistory(prev => [...prev, userMessage]);

    // If no target node, this is an end action
    if (!option.targetNodeId) {
      const botMessage = {
        type: 'bot',
        text: 'Thank you! Please use the navigation below to continue.',
        timestamp: new Date()
      };
      setConversationHistory(prev => [...prev, botMessage]);
      return;
    }

    // Load the next node
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/bungyman/node/${option.targetNodeId}`);
      
      // Save current node to history
      setNodeHistory(prev => [...prev, currentNode]);
      
      // Set new current node
      setCurrentNode(response.data);
      
      // Add bot response to conversation
      const botMessage = {
        type: 'bot',
        text: response.data.text,
        timestamp: new Date()
      };
      setConversationHistory(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Failed to load node:', error);
      const errorMessage = {
        type: 'bot',
        text: 'Sorry, something went wrong. Please try again.',
        timestamp: new Date()
      };
      setConversationHistory(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (nodeHistory.length === 0) return;
    
    const previousNode = nodeHistory[nodeHistory.length - 1];
    setCurrentNode(previousNode);
    setNodeHistory(prev => prev.slice(0, -1));
    
    // Remove last bot and user messages from conversation
    setConversationHistory(prev => prev.slice(0, -2));
  };

  const handleRestart = () => {
    loadWelcomeNode();
    setUserInput('');
  };

  const handleUserInput = async (e) => {
    e.preventDefault();
    
    if (!userInput.trim() || searching) return;

    // Add user message to conversation
    const userMessage = {
      type: 'user',
      text: userInput.trim(),
      timestamp: new Date()
    };
    setConversationHistory(prev => [...prev, userMessage]);
    
    const query = userInput.trim();
    setUserInput('');
    setSearching(true);

    try {
      const response = await axios.post(`${API_URL}/api/bungyman/search`, { query });
      const { type, data } = response.data;

      let botResponse = '';

      if (type === 'booking') {
        // Format booking information
        botResponse = `📋 Booking Details:\n\n`;
        botResponse += `🎫 Booking ID: ${data.bookingId}\n`;
        botResponse += `👤 Name: ${data.customerName}\n`;
        botResponse += `📍 Location: ${data.location?.name || 'N/A'}\n`;
        botResponse += `📅 Date: ${new Date(data.date).toLocaleDateString()}\n`;
        botResponse += `🕐 Time: ${data.timeSlot}\n`;
        botResponse += `👥 Jumpers: ${data.numberOfJumpers}\n`;
        botResponse += `💰 Amount: ₹${data.totalAmount}\n`;
        botResponse += `💳 Payment: ${data.paymentStatus}\n`;
        botResponse += `📊 Status: ${data.bookingStatus}\n`;
        if (data.specialInstructions && data.specialInstructions !== 'None') {
          botResponse += `📝 Instructions: ${data.specialInstructions}`;
        }
      } else if (type === 'keyword') {
        botResponse = data;
      } else if (type === 'database') {
        if (data.length > 0) {
          botResponse = data[0].text;
        } else {
          botResponse = "I found some results but couldn't format them properly. Please try rephrasing your question.";
        }
      } else if (type === 'no_match') {
        botResponse = data.message + '\n\n' + data.suggestions.map((s, i) => `${i + 1}. ${s}`).join('\n');
      }

      const botMessage = {
        type: 'bot',
        text: botResponse,
        timestamp: new Date()
      };
      setConversationHistory(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Search error:', error);
      const errorMessage = {
        type: 'bot',
        text: 'Sorry, I encountered an error. Please try again or choose from the options below.',
        timestamp: new Date()
      };
      setConversationHistory(prev => [...prev, errorMessage]);
    } finally {
      setSearching(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
        onClick={onClose}
      />

      {/* Chatbot Container - Mobile First */}
      <div className={`fixed z-50 bg-gray-900 shadow-2xl transition-all duration-300 
        ${isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-full'}
        bottom-0 left-0 right-0 h-full w-full rounded-none
        md:bottom-6 md:right-6 md:left-auto md:w-96 md:h-[650px] md:rounded-2xl md:border md:border-gray-700`}>
        
        {/* Header */}
        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 p-4 md:rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center overflow-hidden border-2 border-white">
              <img 
                src="https://customer-assets.emergentagent.com/job_altitude-chat/artifacts/ch1bj3g7_WhatsApp%20Image%202025-11-30%20at%2000.22.11_68f32bab.jpg"
                alt="BungyMan"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h3 className="text-black font-normal text-base">BungyMan</h3>
              <p className="text-black text-xs font-normal opacity-80">Always here to help</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-black hover:bg-black hover:bg-opacity-10 p-2 rounded-full transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Messages Area */}
        <div className="overflow-y-auto p-3 space-y-3 pb-24" style={{ height: 'calc(100% - 200px)' }}>
          {conversationHistory.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] p-2 rounded-xl ${
                  message.type === 'user'
                    ? 'bg-yellow-500 text-black rounded-br-none'
                    : 'bg-gray-800 text-white rounded-bl-none'
                }`}
              >
                <p className="whitespace-pre-wrap text-xs font-normal">
                  <ChatbotMessage text={message.text} />
                </p>
                <span className="text-[10px] opacity-60 mt-1 block font-normal">
                  {message.timestamp.toLocaleTimeString('en-US', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </span>
              </div>
            </div>
          ))}
          
          {loading && (
            <div className="flex justify-start">
              <div className="bg-gray-800 p-3 rounded-2xl rounded-bl-none">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
          )}
          
          {/* Invisible element at the bottom for auto-scroll */}
          <div ref={messagesEndRef} />
        </div>

        {/* Input & Options Area */}
        <div className="absolute bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-700 p-2">
          {/* Action Buttons */}
          <div className="flex gap-1 mb-2">
            {nodeHistory.length > 0 && (
              <button
                onClick={handleBack}
                className="flex items-center gap-1 px-2 py-1 text-xs font-normal bg-gray-700 text-white rounded-md hover:bg-gray-600 transition-colors"
              >
                <ArrowLeft size={12} />
                Back
              </button>
            )}
            <button
              onClick={handleRestart}
              className="flex items-center gap-1 px-2 py-1 text-xs font-normal bg-gray-700 text-white rounded-md hover:bg-gray-600 transition-colors"
            >
              <RotateCcw size={12} />
              Restart
            </button>
          </div>

          {/* Options - Now above input */}
          {currentNode && currentNode.options && currentNode.options.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {currentNode.options
                .sort((a, b) => a.order - b.order)
                .map((option) => (
                  <button
                    key={option.id}
                    onClick={() => handleOptionClick(option)}
                    disabled={loading || searching}
                    className="flex-1 min-w-[calc(50%-0.25rem)] p-1 text-xs font-normal bg-gray-800 text-white rounded-lg hover:bg-yellow-500 hover:text-black transition-all duration-200 text-center disabled:opacity-50 disabled:cursor-not-allowed border border-gray-700 hover:border-yellow-500"
                  >
                    {option.text}
                  </button>
                ))}
            </div>
          )}

          {/* Text Input Form - Now at bottom */}
          <form onSubmit={handleUserInput}>
            <div className="flex gap-1">
              <input
                ref={inputRef}
                type="text"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                placeholder="Type your question or booking ID..."
                disabled={searching || loading}
                className="flex-1 px-2 py-1.5 text-xs font-normal bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-yellow-500 focus:outline-none placeholder-gray-500 disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={!userInput.trim() || searching || loading}
                className="px-2 py-1.5 bg-yellow-500 text-black rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {searching ? (
                  <div className="w-3 h-3 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Send size={14} />
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default BungyManChatbot;
